import { compareSemver, trimAppVersion } from '../services/version-check.ios-lookup';

// Pure decision core for native backend routing (#4280). Given this
// build's own version and the live production version, decide whether to
// talk to the canary/staging backend or the apex/live backend. Kept
// Angular- and DOM-free so the whole decision unit-tests under plain Node
// (mirrors the version-check.ios-lookup.ts split). See
// design-docs/backend-routing.md.
//
// 'apex'   = the live production backend (mh-physique.app)
// 'canary' = the staging backend (canary.mh-physique.app)
// This is the one route type across the app; util/backend-origin.ts maps
// it to the concrete host. (It replaces #4258's `InstallRoute`, which was
// removed together with the rest of that routing subsystem.)
// A string-literal union, not an enum, on purpose: these values appear
// verbatim as the `routed_to` Sentry tag, so a mapping layer would only add
// drift risk (same rationale the removed `InstallEnvStatus` union carried).
export type BackendRoute = 'apex' | 'canary';

// Reads the "commits past the tag" distance out of a `git describe`
// string. `v1.29.9-3-abcd` -> 3. An exactly-tagged build (`v1.29.9`, or
// the served `v1.29.9-prod`) has no numeric distance field, so it is
// distance 0. We coerce a missing / non-numeric field to 0 (mirroring
// compareSemver's `|| 0`) so a distanced client still sorts newer than
// an exactly-tagged live and never compares against NaN.
function parseCommitDistance(raw: string): number {
  const noV = raw.startsWith('v') ? raw.slice(1) : raw;
  // the field right after the tag is the distance; it is the only field
  // that is all-digits (the tag is dotted, the hash is hex+letters, the
  // env is letters)
  const afterTag = noV.split('-')[1];
  if (afterTag !== undefined && /^\d+$/.test(afterTag)) {
    return parseInt(afterTag, 10);
  }
  return 0;
}

// Decide the backend for a native prod build. The client build is a
// pre-release build exactly when it is NEWER than the live release, so:
//   client newer than live -> canary
//   client equal or older   -> apex   (apex still serves old clients)
// "Newer" is a two-step compare: first the semver tag, then — when the
// tags are equal — the commit distance past that tag (so an un-bumped
// local/CI build that is ahead of live still routes to canary). See
// design-docs/backend-routing.md.
export function decideNativeRoute(clientVersion: string, liveVersion: string): BackendRoute {
  // if the tags differ, the tag decides
  const tagCmp = compareSemver(trimAppVersion(clientVersion), trimAppVersion(liveVersion));
  if (tagCmp > 0) return 'canary';
  if (tagCmp < 0) return 'apex';
  // if the tags are equal, the commit distance breaks the tie
  const clientDistance = parseCommitDistance(clientVersion);
  const liveDistance = parseCommitDistance(liveVersion);
  return clientDistance > liveDistance ? 'canary' : 'apex';
}
